// libs
import React, { useCallback } from 'react';
import { differenceBy } from 'lodash';
import difference from 'lodash/difference';
import isEmpty from 'lodash/isEmpty';
import isEmail from 'validator/lib/isEmail';

import { TFunction, Trans } from '@lyearn/i18n';
//components
import { At } from '@lyearn/icons';
import { Typography } from '@lyearn/ui';
import { Fields, FieldType, useFormFieldError, useFormFieldValue } from '@/components/Form';
import { FetchOrgRolesFunction } from '@/modules/corporate/modules/learners/views/LearnerManager/components/Invite/Learner/Dialog/components/ManualInvite/Editor/DepartmentRole/hooks/useFetchOrgRoles';
import { UpdateUserInput } from '@/types/schema';

import { LearnerEditProfileDetailsFragment } from '../../../graphql/queries/generated/learnerEditProfileDetails';
import AdditionalDetails from './AdditionalDetails';

export type EditProfileInput = UpdateUserInput & {
  responsibilityIds?: { initialValues?: string[] };
};

export const adaptEditProfileInputOnSubmit = (
  values: EditProfileInput,
  isDesignationEnabled: boolean,
  learner: LearnerEditProfileDetailsFragment,
): UpdateUserInput => {
  const responsibilityIds: UpdateUserInput['responsibilityIds'] = {
    add: difference(
      values.responsibilityIds?.add ?? [],
      values.responsibilityIds?.initialValues ?? [],
    ),
    remove: difference(
      values.responsibilityIds?.initialValues ?? [],
      values.responsibilityIds?.add ?? [],
    ),
  };

  if (!isDesignationEnabled) {
    let orgRoleId = '';
    if (responsibilityIds.add?.length) {
      orgRoleId = responsibilityIds.add[0];
    } else {
      orgRoleId = differenceBy(
        learner.responsibilities?.map((responsibilities) => responsibilities.id) ?? [],
        responsibilityIds.remove ?? [],
      )[0];
    }
    if (orgRoleId && values.orgRole) {
      values.orgRole.set = orgRoleId;
    }
  }

  return { ...values, responsibilityIds };
};

export const getFormFields = (t: TFunction, learner: LearnerEditProfileDetailsFragment): Fields => {
  return {
    emailId: {
      name: 'emailId',
      label: (
        <Typography className="ms-8" color="text-text-primary" variant="body-short-02">
          {t('Admin:InviteStaff.EmailAddress')}
        </Typography>
      ),
      type: FieldType.Text,
      disabled: true,
      size: 'large',
      props: {
        endAdornment: <At className="h-24 w-24" fillColorToken="icon-placeholder" />,
      },
    },
    name: {
      name: 'name',
      label: (
        <Typography className="ms-8" color="text-text-primary" variant="body-short-02">
          {t('Common:Name')}
        </Typography>
      ),
      placeholder: 'Employee Name',
      type: FieldType.Text,
      size: 'large',
    },

    additionalDetails: {
      name: 'additionalDetails',
      type: FieldType.SlowCustom,
      Component: ({ setFieldValue, errors }) => {
        const orgRoleId = useFormFieldValue('orgRole.set');
        const reportsToUserId = useFormFieldValue('reportsToUserId');
        const departmentId = useFormFieldValue('department.set');
        const responsibilityIds = useFormFieldValue('responsibilityIds.add');
        const responsibilityIdsError = useFormFieldError('responsibilityIds.add');
        const onChange = useCallback(
          (
            fieldKey:
              | 'orgRole.set'
              | 'reportsToUserId'
              | 'department.set'
              | 'responsibilityIds.add',
            nextValue?: string | string[] | null,
          ) => {
            setFieldValue(fieldKey, nextValue, true);
          },
          [setFieldValue],
        );

        return (
          <AdditionalDetails
            errors={{
              reportsToUserId: errors.reportsToUserId,
              responsibilityIds: responsibilityIdsError,
            }}
            learner={learner}
            values={{ orgRoleId, reportsToUserId, departmentId, responsibilityIds }}
            onChange={onChange}
          />
        );
      },
    },
  };
};

export const FORM_LAYOUT = ['emailId', 'name', 'additionalDetails'];

export const validateForm =
  (learner: LearnerEditProfileDetailsFragment, fetchOrgRoles: FetchOrgRolesFunction) =>
  async (values: EditProfileInput) => {
    const errors: any = {};
    const reporteeIds = learner.reportees?.map((reportee) => reportee.id);

    if (isEmpty(values.emailId)) {
      errors.emailId = <Trans i18nKey="Admin:InviteStaff.EmailEmptyError" />;
    } else if (!isEmail(values.emailId!)) {
      errors.emailId = <Trans i18nKey="Admin:InviteStaff.EmailInvalidError" />;
    }

    if (isEmpty(values.name)) {
      errors.name = <Trans i18nKey="Admin:InviteStaff.NameEmptyError" />;
    }

    if (reporteeIds?.length && reporteeIds.includes(values.reportsToUserId!)) {
      errors.reportsToUserId = 'dsdds';
    }

    const invalidResponsibilitesError = undefined;
    if (invalidResponsibilitesError) {
      errors['responsibilityIds.add'] = (
        <Trans
          i18nKey="Admin:InviteStaff.ResponsibilityErrorMessage"
          values={{ tracks: invalidResponsibilitesError }}
        />
      );
    }

    return errors;
  };
